import { defineWidgetCore } from "../../widgetDefinition";
import { resolveTemplate } from "../../Utils/resolveTemplate";
import type { RadarComponentSpec, WidgetResponseSpec } from "../../Processing/types";
import { processRadar } from "../../Processing/radarPipeline";
import { renderChart } from "../../PDF/renderers/chartPdfRenderer";
import { formatBuilderDateRange } from "../../PDF/snapshotDateRange";

export const radarCore = defineWidgetCore<RadarComponentSpec>({
  process(spec, response) {
    const s = spec;
    return {
      type: "radar",
      spec: s,
      radar: processRadar(s, response as WidgetResponseSpec | null),
    };
  },
  pdf: {
    // Captured like the other Highcharts widgets. The live widget overrides getSVG to
    // serialize its DOM (gradients are drawn outside Highcharts), so the target size is
    // advisory in the browser and exact in the headless renderer.
    capture: "highcharts",
    buildSnapshot({ processed, slicerValues }) {
      if (processed.type !== "radar") return null;
      const spec = processed.spec as import("../../Processing/types").RadarComponentSpec;
      const vertexDimension =
        spec.vertexDimension ??
        (spec.radar_builder_misc?.vertexDimension as string | undefined) ??
        "metric";
      const DEFAULT_TITLES: Record<string, string> = {
        metric: "Radar Chart",
        player: "Player Radar",
        position: "Position Radar",
        drill: "Drill Radar",
      };
      const title =
        resolveTemplate(spec.display?.title ?? "", slicerValues).resolved ||
        DEFAULT_TITLES[vertexDimension] ||
        "Radar Chart";
      const summary = formatBuilderDateRange(spec.radar_builder_misc) || null;
      return { type: "chart" as const, title, summary };
    },
    render({ id, snapshot, rect, scale, chartImages }) {
      if (snapshot.type !== "chart") return null;
      return renderChart(snapshot, rect, scale, chartImages[id]);
    },
  },
});
